import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputAdornment,
  IconButton,
} from '@mui/material';
import logoIcon from '../../../../assets/common-assets/docu-transcribe-logo(1).png';
import Visibility from '../../../../assets/common-assets/eye.svg';
import VisibilityOff from '../../../../assets/common-assets/eye-off.svg';
import {
  getHelperText,
  INVALID_INPUT_REGEX,
  RESET_PASSWORD,
} from '../../../../constants/staticContent';
import { classes } from './utils';
import { lightPalette } from '../../../../theme';

const SetNewPassword = ({
  formData,
  setFormData,
  errors,
  setErrors,
  submitForm,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConformPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConformPassword = () =>
    setShowConformPassword((show) => !show);

  const validatePassword = (password) => {
    const passwordRegex = INVALID_INPUT_REGEX.PASSWORD;
    return passwordRegex.test(password);
  };

  const validateEmptyPassword = (password) => password !== '';

  const newConfirmSimilarity = (newPass, confirmPass) =>
    newPass === confirmPass;
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));

    if (name === 'newPassword') {
      const isValid = validatePassword(value);
      setErrors((prevErrors) => ({
        ...prevErrors,
        newPassword: !isValid,
      }));
    } else if (name === 'confirmPassword') {
      const isValid = value === formData.newPassword;
      setErrors((prevErrors) => ({
        ...prevErrors,
        confirmPassword: !isValid,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newPasswordValid = validatePassword(formData.newPassword);
    const confirmPasswordValid = validateEmptyPassword(
      formData.confirmPassword,
    );

    const similarityValid = newConfirmSimilarity(
      formData.newPassword,
      formData.confirmPassword,
    );

    setErrors({
      newPassword: !newPasswordValid,
      confirmPassword: !confirmPasswordValid || !similarityValid,
    });

    if (newPasswordValid && confirmPasswordValid && similarityValid) {
      const { email } = formData;
      const { newPassword } = formData;
      const { confirmPassword } = formData;
      submitForm({
        email,
        newPassword,
        confirmPassword,
      });
    }
  };

  return (
    <Grid
      item
      xs={12}
      sm={6.5}
      md={7.5}
      className={classes.rightGridForAllStep}
    >
      <Box className={classes.rightGridMainBoxForAllStep}>
        <Box
          className={classes.logoBox}
          component="img"
          src={logoIcon}
          alt="logoIcon"
        />
        <Typography className={classes.resetPasswordHeader} variant="h4">
          {RESET_PASSWORD.HEADER}
        </Typography>
        <Typography
          variant="subtitle1"
          className={classes.rightBoxInfoSubtitleForAllStep}
          color={lightPalette.carbonFiber.semiDark}
        >
          {RESET_PASSWORD.INFO.STEP3}
        </Typography>
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit}
          className={classes.submitButton}
        >
          <FormControl className={classes.formControl}>
            <TextField
              className={classes.textFieldStepThree}
              required
              label={RESET_PASSWORD.TEXTFIELD_STEP_THREE.NEW_PASSWORD}
              id="newPassword"
              name="newPassword"
              autoComplete="new_password"
              onChange={handleInputChange}
              value={formData.newPassword}
              error={errors.newPassword}
              helperText={getHelperText(
                'newPassword',
                'newPassword',
                errors,
                formData,
              )}
              autoFocus
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? (
                        <Box
                          component="img"
                          className={classes.eyeIconForPassword}
                          src={Visibility}
                          alt="EyeOnIcon"
                        />
                      ) : (
                        <Box
                          component="img"
                          className={classes.eyeIconForPassword}
                          src={VisibilityOff}
                          alt="EyeOffIcon"
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              className={classes.textFieldStepThree}
              label={RESET_PASSWORD.TEXTFIELD_STEP_THREE.CONFIRM_PASSWORD}
              required
              id="confirmPassword"
              name="confirmPassword"
              autoComplete="confirm_password"
              onChange={handleInputChange}
              value={formData.confirmPassword}
              error={errors.confirmPassword}
              helperText={getHelperText(
                'confirmPassword',
                'confirmPassword',
                errors,
                formData,
              )}
              type={showConfirmPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowConformPassword}
                      edge="end"
                    >
                      {showConfirmPassword ? (
                        <Box
                          component="img"
                          className={classes.eyeIconForPassword}
                          src={Visibility}
                          alt="EyeOnIcon"
                        />
                      ) : (
                        <Box
                          component="img"
                          className={classes.eyeIconForPassword}
                          src={VisibilityOff}
                          alt="EyeOffIcon"
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              className={classes.formButtons}
            >
              <Typography variant="h5">
                {RESET_PASSWORD.SUBMIT_BUTTON}
              </Typography>
            </Button>
          </FormControl>
        </Box>
      </Box>
    </Grid>
  );
};

SetNewPassword.propTypes = {
  formData: PropTypes.shape({
    email: PropTypes.string,
    verificationCode: PropTypes.string,
    password: PropTypes.string,
    newPassword: PropTypes.string,
    confirmPassword: PropTypes.string,
  }).isRequired,
  setFormData: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    newPassword: PropTypes.bool,
    confirmPassword: PropTypes.bool,
  }).isRequired,
  setErrors: PropTypes.func.isRequired,
  submitForm: PropTypes.func.isRequired,
};
export default SetNewPassword;
